import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Bot, ArrowRight, HelpCircle, X } from 'lucide-react'
import { describeEscalationAlert } from '@/lib/escalationAlertCopy'
import { conversationSocket, ConversationEscalatedPayload, ConversationUpdatedPayload } from '@/services/conversationSocket'

/**
 * Reproduce un tono de alerta suave y profesional sintetizado vía Web Audio API.
 * No requiere descargar archivos .mp3 externos y tiene 0ms de latencia.
 */
function playEscalationChime(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return
    const ctx = new AudioContextClass()
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {})
    }

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    // Acorde de atención: D5 (587.33 Hz) pasando a A5 (880.00 Hz)
    const now = ctx.currentTime
    osc.frequency.setValueAtTime(587.33, now)
    osc.frequency.setValueAtTime(880.0, now + 0.08)

    gain.gain.setValueAtTime(0.22, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45)

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.start(now)
    osc.stop(now + 0.45)
  } catch {
    // Si el navegador bloquea autoplay hasta interacción de usuario, ignorar silenciosamente
  }
}

/**
 * Hook global que escucha las elevaciones de casos realizadas por el Agente IA
 * y notifica en tiempo real a los vendedores con alerta visual y auditiva.
 */
export function useEscalationNotifications(): void {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const recentAlertsRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    const handleEscalationAlert = (data: {
      conversationId: string
      customerName?: string | null
      customerPhone?: string | null
      reason?: string
      summary?: string | null
    }) => {
      const now = Date.now()
      const lastAlert = recentAlertsRef.current.get(data.conversationId)
      // Evitar alertas duplicadas en ráfaga (5 segundos de cooldown por conversación)
      if (lastAlert && now - lastAlert < 5000) {
        return
      }
      recentAlertsRef.current.set(data.conversationId, now)

      // 1. Alerta auditiva para el vendedor
      playEscalationChime()

      // 2. Invalidar caché para actualizar inmediatamente listas y contadores
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation', data.conversationId] })

      const customerLabel = data.customerName || data.customerPhone || 'Cliente'
      const copy = describeEscalationAlert({ reason: data.reason, summary: data.summary })
      const AlertIcon = copy.isConsultation ? HelpCircle : Bot

      // 3. Notificación interactiva en el frontend
      toast.custom(
        (t) => (
          <div
            role="alert"
            className={`${
              t.visible ? 'animate-enter' : 'animate-leave'
            } pointer-events-auto flex w-full max-w-md items-center justify-between rounded-2xl border-l-4 border-purple-600 bg-white p-3.5 shadow-xl ring-1 ring-black/5 sm:p-4`}
          >
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-700">
                <AlertIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-purple-800">
                    {copy.badge}
                  </span>
                  <span className="text-[11px] font-bold text-slate-900">
                    {copy.title}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-sm font-bold text-slate-800">
                  {customerLabel}
                </p>
                <p className="line-clamp-2 text-xs text-slate-500">
                  {copy.detail}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 ml-2 border-l border-slate-100 pl-2">
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id)
                  navigate(`/app/conversations/${data.conversationId}`)
                }}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-purple-700 active:scale-95 transition-all"
              >
                <span>{copy.actionLabel}</span>
                <ArrowRight className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label="Cerrar notificación"
                onClick={() => toast.dismiss(t.id)}
                className="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ),
        {
          id: `escalation-${data.conversationId}`,
          duration: copy.isConsultation ? 20000 : 9000,
          position: 'top-right',
        }
      )
    }

    const unsubscribe = conversationSocket.subscribe({
      onConversationEscalated: (event: ConversationEscalatedPayload) => {
        if (event?.conversationId) {
          handleEscalationAlert({
            conversationId: event.conversationId,
            customerName: event.customerName,
            customerPhone: event.customerPhone,
            reason: event.reason,
            summary: event.summary,
          })
        }
      },
      onConversationUpdated: (event: ConversationUpdatedPayload) => {
        // También capturar si conversation:updated vino con marca de escalación o modo humano por bot
        if (
          event?.conversationId &&
          (event.escalated || (event.mode === 'human' && event.escalatedBy === 'ai_agent'))
        ) {
          handleEscalationAlert({
            conversationId: event.conversationId,
            customerName: event.customerName,
            customerPhone: event.customerPhone,
            reason: event.reason,
            summary: event.summary,
          })
        }
      },
    })

    return () => {
      unsubscribe()
    }
  }, [navigate, queryClient])
}
